/*******************************************************************************
 * Copyright     : Copyright (c)  2021 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : FilesTable.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : ConfigTable component
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422335
 *
 * @Date         : December 27, 2021
 *
 * @Version      : Version 1.0
 *
 * By            : <Changed By user id>
 * On            :  <Change Date>
 * Description   : <Change Description>
 *
 *
 ******************************************************************************/
import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { DataTable } from "@scuf/datatable";
import { Grid, Icon } from "@scuf/common";
import {
  fetchSelectedApmRecord,
  setCurrentWorkFlowType,
  setCurrentWorkFlowStep,
} from "stores/ApmWorkFlowStore";
import {
  IApmFileData,
  EnumApmSettingsWorkFlow,
  EnumCurrentApmFileRecordStatus,
} from "models/configuration";
import { StatusColumnRenderer } from "../StatusColumnRenderer";

import { COLUMN_HEADER, HEADER_BAR, PLACEHOLDER_CONTENT } from "../constants";
import { selectCustomer } from "stores/CustomerStore";
import { appConfig } from "shared/utils";
import "./ApmFilesTable.scss";

interface IApmFilesTableProps {
  data: IApmFileData[];
  loading: boolean;
}

interface IApmFilesHistoryData {
  apmFileRecords: IApmFileData[] | [];
  totalFileCount: number;
}

export const ApmFilesTable: FC<IApmFilesTableProps> = ({ data, loading }) => {
  const [apmFilesTableData, setApmFilesTableData] =
    useState<IApmFilesHistoryData>({ apmFileRecords: [], totalFileCount: 0 });
  const [initialApmFilesData, setInitialApmFilesData] =
    useState<IApmFilesHistoryData>({
      apmFileRecords: data,
      totalFileCount: data.length,
    });
  const [selectedRow, setSelectedRow] = useState<IApmFileData[]>([]);
  const selectedCustomer = useSelector(selectCustomer);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const updatedFilesList = data.map((item) => ({
      ...item,
      createdOn: moment(parseInt(item.createdOn)).format("DD MMM YYYY"),
    }));

    const updatedData = {
      apmFileRecords: updatedFilesList,
      totalFileCount: updatedFilesList.length,
    };

    setApmFilesTableData(updatedData);
    setInitialApmFilesData(updatedData);
    setSelectedRow([]);
  }, [data]);

  const onGlobalFilterHandler = (query: string) => {
    if (query.trim().length === 0) {
      setApmFilesTableData(initialApmFilesData);
    } else {
      const checkMatch = (stringToMatch: string) => {
        return stringToMatch.toLowerCase().includes(query.toLowerCase());
      };

      const filteredData = initialApmFilesData.apmFileRecords.filter(
        ({ fileName, platform, tailNumber, serialNumber, createdOn, status }) =>
          (fileName ? checkMatch(fileName) : false) ||
          checkMatch(platform) ||
          checkMatch(tailNumber) ||
          checkMatch(serialNumber) ||
          checkMatch(createdOn) ||
          checkMatch(status)
      );
      setApmFilesTableData({
        apmFileRecords: filteredData,
        totalFileCount: filteredData.length,
      });
    }
  };

  const onEditClickHandler = () => {
    dispatch(fetchSelectedApmRecord(selectedRow[0].historyId));
    dispatch(setCurrentWorkFlowType(EnumApmSettingsWorkFlow.Edit));
    navigate("/new");
    if (selectedRow[0].previousScreen !== null) {
      dispatch(setCurrentWorkFlowStep(parseInt(selectedRow[0].previousScreen)));
    } else {
      dispatch(setCurrentWorkFlowStep(1));
    }
  };

  const onPreviewClickHandler = () => {
    dispatch(fetchSelectedApmRecord(selectedRow[0].historyId));
    dispatch(setCurrentWorkFlowType(EnumApmSettingsWorkFlow.Preview));
    navigate("/preview");
  };

  const onDownloadClickHandler = () => {
    window.open(
      `${appConfig.fel_endpoint}?customerSeq=${selectedCustomer?.customerId}&partNumber=${selectedRow[0].fileName}`
    );
  };

  return (
    <Grid className="apm-file-history-component">
      <Grid.Row>
        <Grid.Column>
          <DataTable
            className="apm-file-history-table"
            data={apmFilesTableData?.apmFileRecords}
            search={true}
            loading={loading}
            scrollHeight="712.5px"
            selection={selectedRow}
            onSelectionChange={(selectedData: IApmFileData[]) =>
              setSelectedRow(selectedData)
            }
            selectionMode="single"
            searchPlaceholder={PLACEHOLDER_CONTENT.searchFor}
            onGlobalFilter={onGlobalFilterHandler}
            resizableColumns={true}
            totalRecords={apmFilesTableData?.apmFileRecords.length}
          >
            <DataTable.ActionBar clearSelectionOnClose={true}>
              <DataTable.ActionBar.Item
                icon={<Icon name="edit" root="common" />}
                content={HEADER_BAR.edit}
                disabled={
                  selectedRow.length > 0 &&
                  selectedRow[0].status ===
                    EnumCurrentApmFileRecordStatus.completed
                }
                onClick={onEditClickHandler}
              />
              <DataTable.ActionBar.Item
                icon={<Icon name="doc-search" root="building" />}
                content={HEADER_BAR.preview}
                onClick={onPreviewClickHandler}
              />
              <DataTable.ActionBar.Item
                icon={<Icon name="file-download" root="global" />}
                content={HEADER_BAR.download}
                onClick={onDownloadClickHandler}
                disabled={
                  selectedRow.length > 0 &&
                  selectedRow[0].status ===
                    EnumCurrentApmFileRecordStatus.inProgress
                }
              />
            </DataTable.ActionBar>
            <DataTable.Column
              initialWidth="18%"
              align="left"
              field="fileName"
              placeholder="- -"
              header={COLUMN_HEADER.fileName}
              sortable={true}
            />
            <DataTable.Column
              initialWidth="15%"
              align="left"
              field="platform"
              header={COLUMN_HEADER.aircraftModel}
              sortable={true}
            />
            <DataTable.Column
              initialWidth="17%"
              align="left"
              field="tailNumber"
              header={COLUMN_HEADER.tailNumber}
              sortable={true}
            />
            <DataTable.Column
              initialWidth="15%"
              align="left"
              field="serialNumber"
              header={COLUMN_HEADER.serialNumber}
              sortable={true}
            />
            <DataTable.Column
              initialWidth="15%"
              align="left"
              field="createdOn"
              header={COLUMN_HEADER.createdOn}
              sortable={true}
            />
            <DataTable.Column
              initialWidth="15%"
              align="center"
              field="status"
              header={COLUMN_HEADER.status}
              sortable={true}
              renderer={(cellData) => (
                <StatusColumnRenderer cellData={cellData} />
              )}
            />

            <DataTable.Pagination
              itemsPerPage={5}
              totalItems={apmFilesTableData?.totalFileCount}
              showDisplayDetails
              showNav
            />
          </DataTable>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};
